/**
 * One category as a board of subcategory tiles.
 *
 * Used by both the dashboard and the category page, so the two cannot drift.
 * The dashboard stacks one per category and adds collapse and reorder handles;
 * the category page shows a single board with a larger header.
 *
 * The tile is the unit of work here: it carries its own status, its own add
 * button, and its items with their statuses. That is what makes this layout
 * different from a list -- everything you need to bring one area up to date is
 * inside one card.
 */

import { Link } from 'react-router-dom'

import type { Category, Item, ItemPriority, Subcategory } from '@/api/types'
import { BoardItem, BoardTile, TileEmpty, TileOverdue } from '@/components/BoardTile'
import { InlineStatus } from '@/components/InlineStatus'
import { DragHandle, SortableList } from '@/components/Sortable'
import { onTintPill } from '@/components/ui'
import { categoryHeaderStyle, subcategoryHeaderStyle } from '@/lib/color'
import { formatDue, pluralize } from '@/lib/format'

export interface CategoryBoardProps {
  category: Category
  /** Items across the whole app; the board selects its own. */
  items: Item[]
  today: string
  onSubcategoryStatus: (sub: Subcategory, next: string | null) => void | Promise<void>
  onItemStatus: (item: Item, next: string | null) => void | Promise<void>
  onComplete: (item: Item) => void
  onPriorityChange: (item: Item, priority: ItemPriority) => void
  onAddTask: (subcategoryId: number) => void
  /** Dashboard only: collapse the whole band. */
  collapsed?: boolean
  onToggleCollapse?: () => void
  /** Reorder the subcategory tiles within this category. */
  onReorderSubcategories?: (orderedIds: number[]) => void
  /** Rendered at the far left of the band, for reordering the categories
   *  themselves. Supplied by the dashboard only. */
  dragHandle?: React.ReactNode
  /** Larger header for the category page, where it is the subject of the page. */
  size?: 'page' | 'section'
}

export function CategoryBoard({
  category,
  items,
  today,
  onSubcategoryStatus,
  onItemStatus,
  onComplete,
  onPriorityChange,
  onAddTask,
  collapsed = false,
  onToggleCollapse,
  onReorderSubcategories,
  dragHandle,
  size = 'section',
}: CategoryBoardProps) {
  const head = categoryHeaderStyle(category.color, category.id)
  const tint = subcategoryHeaderStyle(category.color, category.id)

  const activeSubs = category.subcategories.filter((sub) => !sub.is_archived)
  const subIds = new Set(activeSubs.map((sub) => sub.id))
  const categoryItems = items.filter((item) => subIds.has(item.subcategory.id))

  // Every subcategory gets a tile, including empty ones: the tile is where its
  // add button, its status note and its reorder handle live, so hiding it
  // would put those out of reach. Only the priority tiles in "Everything open"
  // disappear when empty, where there is nothing to reach.
  const itemsFor = (subId: number) =>
    categoryItems.filter((item) => item.subcategory.id === subId)

  return (
    <section aria-labelledby={`category-${category.id}`} className="flex flex-col gap-3">
      {/* Column on a phone, row from sm up. Side by side on a narrow screen
          the counts crowded the name into a two-character truncation, so the
          one thing the band exists to say was the thing you could not read. */}
      <header
        style={{ backgroundColor: head.background, color: head.text }}
        className={[
          'flex flex-col gap-2 rounded-lg shadow-md sm:flex-row sm:flex-wrap sm:items-center sm:gap-3',
          size === 'page' ? 'px-5 py-5' : 'px-4 py-3',
        ].join(' ')}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {dragHandle}

          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-expanded={!collapsed}
              aria-controls={`category-${category.id}-tiles`}
              aria-label={collapsed ? `Expand ${category.name}` : `Collapse ${category.name}`}
              style={{ color: head.text }}
              className={`shrink-0 text-lg transition-transform ${collapsed ? '' : 'rotate-90'}`}
            >
              <span aria-hidden>&#9656;</span>
            </button>
          )}

          {/* Wraps rather than truncates: a phone is narrow enough that a
              truncated category name is often unrecognisable. */}
          <h2
            id={`category-${category.id}`}
            className={`min-w-0 flex-1 font-bold tracking-tight ${
              size === 'page' ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'
            }`}
          >
            {category.name}
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="pill border" style={onTintPill(head.text)}>
            {activeSubs.length} {pluralize(activeSubs.length, 'subcategory', 'subcategories')}
          </span>
          <span className="pill border" style={onTintPill(head.text)}>
            {categoryItems.length} open {pluralize(categoryItems.length, 'item')}
          </span>
        </div>
      </header>

      {!collapsed && (
        <div id={`category-${category.id}-tiles`}>
          {activeSubs.length === 0 ? (
            <p className="card px-4 py-6 text-center text-sm text-ink-3">
              No subcategories yet. Items live in a subcategory, so add one in{' '}
              <Link to="/settings" className="font-medium text-navy hover:underline">
                Settings
              </Link>
              .
            </p>
          ) : (
            // items-start is load-bearing: without it grid rows equalise height
            // and a short tile stretches to match the tallest beside it.
            <SortableList
              items={activeSubs}
              getId={(sub) => sub.id}
              onReorder={(ids) => onReorderSubcategories?.(ids)}
              layout="grid"
              className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-3"
            >
              {(sub, drag) => {
                const subItems = itemsFor(sub.id)
                const overdue = subItems.filter(
                  (item) => formatDue(item.due_date, today)?.overdue,
                ).length

                return (
                  <BoardTile
                    title={sub.name}
                    titleId={`sub-${sub.id}`}
                    count={subItems.length}
                    tint={tint}
                    onAddTask={() => onAddTask(sub.id)}
                    addLabel={`Add a task to ${sub.name}`}
                    containerRef={drag.setNodeRef}
                    style={drag.style}
                    isDragging={drag.isDragging}
                    dragHandle={
                      onReorderSubcategories && (
                        <DragHandle
                          handleProps={drag.handleProps}
                          label={`Reorder ${sub.name}`}
                          className="-ml-1 self-center"
                        />
                      )
                    }
                    statusSlot={
                      <InlineStatus
                        value={sub.status_note}
                        onSave={(next) => onSubcategoryStatus(sub, next)}
                        tintText={tint.text}
                        label={`Status for ${sub.name}`}
                      />
                    }
                  >
                    <TileOverdue count={overdue} />
                    {subItems.length === 0 ? (
                      <TileEmpty>Nothing open here.</TileEmpty>
                    ) : (
                      <ul className="m-0 list-none p-0">
                        {subItems.map((item) => (
                          <BoardItem
                            key={item.id}
                            item={item}
                            today={today}
                            onComplete={onComplete}
                            onItemStatus={onItemStatus}
                            onPriorityChange={onPriorityChange}
                          />
                        ))}
                      </ul>
                    )}
                  </BoardTile>
                )
              }}
            </SortableList>
          )}
        </div>
      )}
    </section>
  )
}
